import {
  addDays,
  differenceInDays,
  differenceInYears,
  format,
  formatDistanceToNowStrict,
  parseISO,
  isValid,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { t } from 'i18next'
import { format as timeagoFormat } from 'timeago.js'

import { TIME_TYPES } from '../constants/date.constants'
import { getSiteDateFormat } from '../web/utils/date-format-utils'

/**
 * Format a date in one of the site's date-format styles (see
 * `getSiteDateFormat`), honouring the day-first / month-first site setting.
 *
 * @param {string | Date | number | null | undefined} dateInput
 * @param {import('../web/utils/date-format-utils').DateFormatStyle} [style]
 * @param {string} [timezone] Optional IANA timezone (e.g. "Australia/Melbourne").
 *   When provided, ISO datetime/Unix-timestamp inputs are formatted in that
 *   timezone instead of the browser's local zone — required for fields like
 *   `Service.purchasedAt` (TIMESTAMP) so the rendered local date matches the
 *   reporting backend's site-tz formatting (CORE-1213 item 6 / CORE-1226).
 *   Pure date-only strings (YYYY-MM-DD, e.g. Spanner DATE columns) are treated
 *   as calendar days and the timezone arg has no effect on them.
 * @param {string} [pattern] Optional date-fns pattern that replaces the stored site pattern, for
 *   pages such as public links that cannot read the signed-in site's settings.
 * @returns {string} Formatted date string or empty string if invalid
 */
export const formatSiteDate = (dateInput, style = 'short', timezone, pattern) => {
  if (dateInput === null || dateInput === undefined || dateInput === '') {
    return ''
  }

  try {
    let parsedDate
    let isDateOnlyString = false

    if (typeof dateInput === 'string') {
      // Check if it's a Unix timestamp string (all digits)
      if (/^\d+$/.test(dateInput)) {
        parsedDate = new Date(parseInt(dateInput, 10))
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        // Date-only strings (YYYY-MM-DD) must be parsed as local time to avoid
        // timezone shifts: new Date("2026-04-08") parses as UTC midnight, which
        // renders as the previous day in timezones behind UTC.
        parsedDate = new Date(dateInput + 'T00:00:00')
        isDateOnlyString = true
      } else {
        // ISO datetime strings with timezone offset (safe to use new Date)
        parsedDate = new Date(dateInput)
      }
    } else if (typeof dateInput === 'number') {
      parsedDate = new Date(dateInput)
    } else {
      parsedDate = dateInput
    }

    if (Number.isNaN(parsedDate.getTime())) {
      return ''
    }

    const resolvedPattern = pattern ?? getSiteDateFormat(style)

    // For ISO datetimes / Unix timestamps with a caller-provided site tz,
    // render in that zone so the local-day matches the reporting backend.
    // Date-only strings already represent a calendar day with no real
    // timezone — keep using local format() so they're not shifted twice.
    if (timezone && !isDateOnlyString) {
      return formatInTimeZone(parsedDate, timezone, resolvedPattern)
    }
    return format(parsedDate, resolvedPattern)
  } catch {
    return ''
  }
}

/**
 * Format a date with slashes (DD/MM/YYYY for day-first locales, MM/DD/YYYY
 * otherwise — defaults to MM/DD/YYYY when no site setting is present).
 *
 * @param {string | Date | number | null | undefined} dateInput
 * @param {string} [timezone] See `formatSiteDate`.
 * @returns {string}
 */
export const formatDateWithSlashes = (dateInput, timezone) =>
  formatSiteDate(dateInput, 'short', timezone)

/**
 * Format a timestamp into human readable date/time strings.
 * @param {string | Date | null | undefined} timestamp
 * @returns {{ date: string, time: string } | null}
 */
export const formatDateTime = (timestamp, timezone) => {
  if (!timestamp) {
    return null
  }

  let parsedDate

  if (typeof timestamp === 'string') {
    if (/^\d+$/.test(timestamp)) {
      parsedDate = new Date(parseInt(timestamp, 10))
    } else {
      parsedDate = new Date(timestamp)
    }
  } else if (typeof timestamp === 'number') {
    parsedDate = new Date(timestamp)
  } else {
    parsedDate = timestamp
  }

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  if (timezone) {
    return {
      date: formatInTimeZone(parsedDate, timezone, getSiteDateFormat('medium')),
      time: formatInTimeZone(parsedDate, timezone, 'h:mm a'),
    }
  }

  return {
    date: format(parsedDate, getSiteDateFormat('medium')),
    time: format(parsedDate, 'h:mm a'),
  }
}

export const toDateFromTime = (HHmm) => {
  const checkFormat = new RegExp(/^([0-1][0-9]|2[0-3]):([0-5][0-9])$/)
  if (checkFormat.test(HHmm)) {
    const date = new Date()
    const tempTime = HHmm.split(':')
    date.setHours(tempTime[0])
    date.setMinutes(tempTime[1])
    return date
  }
  return null
}

export const weekdaysTimeStartToEnd = (weekdays = {}) => {
  const hasWeekdays = weekdays && Object.keys(weekdays).length > 0
  if (hasWeekdays) {
    return Object.fromEntries(
      Object.entries(weekdays)
        .filter(([_, value]) => !!value)
        .map((day) => [
          day[0],
          {
            startTime: toDateFromTime(day[1].startTime),
            endTime: toDateFromTime(day[1].endTime),
          },
        ])
    )
  }
  return null
}

export const parseDateToTime = (weekdays = {}) => {
  const hasWeekdays = weekdays && Object.keys(weekdays).length > 0
  if (hasWeekdays) {
    return Object.fromEntries(
      Object.entries(weekdays)
        .filter(([_, value]) => !!value)
        .map((day) => [
          day[0],
          {
            startTime: format(day[1].startTime, 'HH:mm'),
            endTime: format(day[1].endTime, 'HH:mm'),
          },
        ])
    )
  }
  return null
}

export const formatDistanceToNowAbbreviated = (date, type = TIME_TYPES.past) => {
  const difference = differenceInDays(new Date(), date)

  if (difference > 30) {
    return `${format(date, getSiteDateFormat('medium'))}`
  }

  const distance = formatDistanceToNowStrict(date)

  const index = distance.indexOf(' ')

  const firstLetter = distance[index + 1]

  const isPast = type === TIME_TYPES.past

  return `${distance.split(' ')[0]}${firstLetter}${isPast ? ` ${t('common:ago')}` : ''}`
}

export const isBirthday = (birthday, datetime, timezone) => {
  if (!datetime) {
    return false
  }
  const formatCheckInDate = formatInTimeZone(datetime, timezone, 'yyyy/MM/dd')

  const year = formatCheckInDate.split('/')[0]
  const month = birthday?.split('-')[1]
  const day = birthday?.split('-')[2].split('T')[0]

  const birthdayCurrentYear = `${year}/${month}/${day}`

  if (birthdayCurrentYear === formatCheckInDate) {
    return true
  }
  return false
}

export const toUtcStartOfDay = (date) => {
  if (!date) {
    return null
  }
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
}

export const toUtcEndOfDay = (date) => {
  if (!date) {
    return null
  }
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999))
}

export const calculateAge = (birthday) => {
  if (!birthday) {
    return null
  }
  return differenceInYears(new Date(), new Date(birthday))
}

export const formatDateWithoutWeek = (date) => {
  if (!date) {
    return ''
  }
  return format(new Date(date), 'MMM dd, yyyy')
}

export const parseFlexibleDate = (value) => {
  if (value == null || value === '') {
    return null
  }
  const raw = String(value)
  const parsed = /^\d+$/.test(raw) ? new Date(Number(raw)) : parseISO(raw)
  return isValid(parsed) ? parsed : null
}

export const dateSortValue = (value) => {
  return parseFlexibleDate(value)?.getTime() ?? 0
}

export const defaultTimeZoneFormat = (date, timezone) => {
  return formatInTimeZone(date, timezone, 'iii d MMM, yyyy')
}

export const defaultTimeZoneFormatWithoutWeek = (date, timezone) => {
  return formatInTimeZone(date, timezone, 'MMM d, yyyy')
}

export const preciseDateFormat = (date, timezone) => {
  return formatInTimeZone(date, timezone, "iii d MMM, yyyy 'at' HH:mm")
}

export const localTimeFormat = (date, timezone, siteTimeFormat) => {
  const pattern = siteTimeFormat === '24-hour' ? 'HH:mm' : 'h:mm a'
  return formatInTimeZone(date, timezone, pattern)
}

export const formatTo12Hour = (date) => {
  if (!date) {
    return ''
  }

  // Check if it's a valid Date object
  if (date instanceof Date && !isNaN(date.getTime())) {
    return format(date, 'hh:mm a')
  }

  // If it's a string, try to parse it
  if (typeof date === 'string') {
    // Handle HH:mm format (24-hour time string like "00:05")
    if (/^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/.test(date)) {
      const [hours, minutes] = date.split(':')
      const tempDate = new Date()
      tempDate.setHours(parseInt(hours, 10))
      tempDate.setMinutes(parseInt(minutes, 10))
      tempDate.setSeconds(0)
      tempDate.setMilliseconds(0)
      return format(tempDate, 'hh:mm a')
    }

    const parsedDate = new Date(date)
    if (!isNaN(parsedDate.getTime())) {
      return format(parsedDate, 'hh:mm a')
    }
  }

  // Return empty string for invalid dates
  return ''
}

// Convert HH:MM time string to Date object for form components
export const timeStringToDate = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return null
  }

  // Handle HH:mm format (24-hour time string)
  if (/^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/.test(timeString)) {
    const [hours, minutes] = timeString.split(':')
    const date = new Date()
    date.setHours(parseInt(hours, 10))
    date.setMinutes(parseInt(minutes, 10))
    date.setSeconds(0)
    date.setMilliseconds(0)
    return date
  }

  return null
}

// Convert Date object to HH:MM time string for storage
export const dateToTimeString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return ''
  }

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Format a date/timestamp as a relative time ago string (e.g., "2 hours ago", "3 days ago")
 * Uses timeago.js for consistent formatting across the application
 *
 * @param {string | number | Date} date - The date to format (ISO string, Unix timestamp in ms, or Date object)
 * @returns {string} Formatted time ago string, or 'recently' if invalid
 *
 * @example
 * formatTimeAgo('2024-01-15T10:30:00Z') // "2 hours ago"
 * formatTimeAgo(1763675120821) // "5 minutes ago"
 * formatTimeAgo('1763675120821') // "5 minutes ago" (Unix timestamp as string)
 * formatTimeAgo(new Date()) // "just now"
 * formatTimeAgo(null) // "recently"
 */
export const formatTimeAgo = (date) => {
  try {
    if (!date) {
      return 'recently'
    }

    let dateObj

    if (typeof date === 'string') {
      // Check if it's a Unix timestamp string (all digits)
      if (/^\d+$/.test(date)) {
        dateObj = new Date(parseInt(date, 10))
      } else {
        // ISO date string
        dateObj = new Date(date)
      }
    } else if (typeof date === 'number') {
      // Unix timestamp as number
      dateObj = new Date(date)
    } else {
      // Already a Date object
      dateObj = date
    }

    if (isNaN(dateObj.getTime())) {
      return 'recently'
    }

    return timeagoFormat(dateObj)
  } catch (error) {
    console.warn('Invalid date for formatTimeAgo:', date, error)
    return 'recently'
  }
}

/**
 * Parse a date-only string (YYYY-MM-DD) to a Date object in local time
 * Avoids timezone shifts by using local midnight
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateOnly = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    return null
  }

  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/
  if (!dateOnlyRegex.test(dateStr)) {
    return null
  }

  try {
    // Parse as local date to avoid timezone shifts
    const date = new Date(dateStr + 'T00:00:00')
    return isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

/**
 * Format a date-only value for display (e.g., "Jun 11, 2026")
 * Handles Date objects or YYYY-MM-DD strings
 * @param {Date|string} dateInput - Date object or date string
 * @returns {string} Formatted date string (e.g., "Jun 11, 2026")
 */
export const formatDateOnlyForDisplay = (dateInput) => {
  if (!dateInput) {
    return ''
  }

  try {
    let date = dateInput instanceof Date ? dateInput : parseDateOnly(dateInput)
    if (!date && typeof dateInput === 'string') {
      const parsed = parseISO(dateInput)
      if (isValid(parsed)) {
        date = parsed
      }
    }
    if (!date) {
      return String(dateInput)
    }

    return format(date, getSiteDateFormat('medium'))
  } catch {
    return String(dateInput)
  }
}

/**
 * Format a Unix timestamp for display (e.g., "Jun 11, 2026")
 * Handles Unix timestamps as strings or numbers, ISO strings, or Date objects
 * @param {string|number|Date} dateInput - Unix timestamp (ms), ISO string, or Date object
 * @returns {string} Formatted date string (e.g., "Jun 11, 2026") or empty string if invalid
 */
export const formatTimestampForDisplay = (dateInput) => {
  if (!dateInput) {
    return ''
  }

  try {
    let dateObj

    if (typeof dateInput === 'string') {
      // Check if it's a Unix timestamp string (all digits)
      if (/^\d+$/.test(dateInput)) {
        dateObj = new Date(parseInt(dateInput, 10))
      } else {
        // ISO date string
        dateObj = new Date(dateInput)
      }
    } else if (typeof dateInput === 'number') {
      // Unix timestamp as number
      dateObj = new Date(dateInput)
    } else if (dateInput instanceof Date) {
      dateObj = dateInput
    } else {
      return ''
    }

    if (isNaN(dateObj.getTime())) {
      return ''
    }

    return format(dateObj, getSiteDateFormat('medium'))
  } catch {
    return ''
  }
}

/**
 * Format a Date object to YYYY-MM-DD for HTML input fields
 * @param {Date|string} dateInput - Date object or date string (YYYY-MM-DD or ISO datetime)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateForInput = (dateInput) => {
  if (!dateInput) {
    return ''
  }

  try {
    let date
    if (dateInput instanceof Date) {
      date = dateInput
    } else {
      // First try YYYY-MM-DD format (avoids timezone shifts)
      date = parseDateOnly(dateInput)
      // Fall back to parsing ISO datetime strings (e.g. "2024-01-15T00:00:00.000Z"
      // stored from Date objects in JSON columns)
      if (!date) {
        const parsed = new Date(dateInput)
        if (!isNaN(parsed.getTime())) {
          date = parsed
        }
      }
    }

    if (!date || isNaN(date.getTime())) {
      return ''
    }

    return format(date, 'yyyy-MM-dd')
  } catch {
    return ''
  }
}

/**
 * Get a date X days from now in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative for past dates)
 * @param {Date} [fromDate=new Date()] - Starting date (defaults to today)
 * @returns {string} Date string in YYYY-MM-dd format
 */
export const getDateXDaysFromNow = (days, fromDate = new Date()) => {
  if (typeof days !== 'number' || isNaN(days)) {
    throw new Error('Days must be a valid number')
  }

  return format(addDays(fromDate, days), 'yyyy-MM-dd')
}

/**
 * Timezone-aware date utilities for frontend operations.
 * These utilities ensure that date comparisons use the site's timezone,
 * not the browser's local timezone.
 */

const ISO_DATE_FORMAT = 'yyyy-MM-dd'

/**
 * Get today's date as a string (YYYY-MM-DD) in the specified timezone
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney', 'America/New_York')
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getTodayInTimezone = (timezone) => {
  return formatInTimeZone(new Date(), timezone, ISO_DATE_FORMAT)
}

/**
 * Check if a date string (YYYY-MM-DD) represents "today" in the specified timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {string} timezone - IANA timezone string
 * @returns {boolean} true if the date is today in the specified timezone
 */
export const isDateTodayInTimezone = (dateString, timezone) => {
  const todayInTimezone = getTodayInTimezone(timezone)
  return dateString === todayInTimezone
}

/**
 * Check if a date string (YYYY-MM-DD) is in the past relative to today in the specified timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {string} timezone - IANA timezone string
 * @returns {boolean} true if the date is before today in the specified timezone
 */
export const isDateBeforeTodayInTimezone = (dateString, timezone) => {
  const todayInTimezone = getTodayInTimezone(timezone)
  return dateString < todayInTimezone
}

/**
 * Check if a date string (YYYY-MM-DD) is today or in the past in the specified timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {string} timezone - IANA timezone string
 * @returns {boolean} true if the date is today or before today in the specified timezone
 */
export const isDateTodayOrBeforeInTimezone = (dateString, timezone) => {
  const todayInTimezone = getTodayInTimezone(timezone)
  return dateString <= todayInTimezone
}

/**
 * Check if a date string (YYYY-MM-DD) is in the future relative to today in the specified timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {string} timezone - IANA timezone string
 * @returns {boolean} true if the date is after today in the specified timezone
 */
export const isDateAfterTodayInTimezone = (dateString, timezone) => {
  const todayInTimezone = getTodayInTimezone(timezone)
  return dateString > todayInTimezone
}

/**
 * Parse a date string or Date object to YYYY-MM-DD format
 * Handles ISO datetime strings by extracting the date part
 * @param {string | Date} date - Date string (YYYY-MM-DD or ISO datetime) or Date object
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const parseDateToISOString = (date) => {
  if (typeof date === 'string') {
    // If it's already YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date
    }
    // If it's an ISO datetime, extract the date part
    return date.split('T')[0]
  }
  // If it's a Date object, convert to YYYY-MM-DD
  return date.toISOString().split('T')[0]
}

/**
 * Get birthday message and formatted date based on input date string
 * @param {string | null | undefined} input - Date string in DD/MM format
 * @param {Function} t - Translation function from i18next
 * @returns {{ message: string, formatted: string } | null} Birthday message and formatted date, or null if invalid
 */
export const getBirthdayMessage = (input, t) => {
  if (!input) {
    return null
  }

  const parts = input.split('/').map(Number)
  const day = parts[0]
  const month = parts[1]

  if (!day || !month) {
    return null
  }

  const today = new Date()
  const currentYear = today.getFullYear()

  let nextBirthday = new Date(currentYear, month - 1, day)
  if (nextBirthday < today) {
    nextBirthday = new Date(currentYear + 1, month - 1, day)
  }

  const diff = differenceInDays(nextBirthday, today)

  if (diff > 7) {
    return null
  }

  let message = ''
  if (diff === 0) {
    message = t('birthday.today')
  } else if (diff === 1) {
    message = t('birthday.tomorrow')
  } else if (diff < 7) {
    message = t('birthday.in_days', { count: diff })
  } else {
    message = t('birthday.in_one_week')
  }

  return {
    message,
    formatted: `${day}/${month}`,
  }
}

/**
 * Check whether a birthday (ISO date string) falls within the next 7 days.
 * @param {string} birthdayDateString - ISO date string (e.g. "1990-04-15T00:00:00Z")
 * @returns {boolean}
 */
export const isBirthdayWithinWeek = (birthdayDateString) => {
  if (typeof birthdayDateString !== 'string' || !birthdayDateString) {
    return false
  }
  const parts = birthdayDateString.split('-')
  const month = parseInt(parts[1], 10)
  const day = parseInt(parts[2]?.split('T')[0], 10)
  if (!day || !month) {
    return false
  }
  const today = new Date()
  const currentYear = today.getFullYear()
  let nextBirthday = new Date(currentYear, month - 1, day)
  if (nextBirthday < today) {
    nextBirthday = new Date(currentYear + 1, month - 1, day)
  }
  return differenceInDays(nextBirthday, today) <= 7
}
